import { computed } from "vue";
import { useToast } from "vue-toastification";
import { i18n } from "../../../i18n";
import {
  instaCategoryId,
  photoCategoryId,
  musicCategoryId,
  customCategoryId,
  accessoryCategoryId,
  giftDedicationPackagingId,
  giftPackagingId,
  engravedOakMassifId,
  engravedBlackOakMassifId,
  bigSizeId,
  giftVoucherIds,
  magneticStand,
  acrylicStandId,
  blackOakMassifId,
} from "../../../config";
import {
  scrollToSection,
  getParamFromUrl,
  updateUrl,
  imageUrlToBase64,
  capitalize,
} from "../../../helpers";
import axios from "../../../axios";
import store from "../../store";
import useRoutes from "../../useRoutes";
import useCart from "../cart/useCart";
import useFileUploader from "../../packages/views/file-uploader/useFileUploader";
import useCropper from "../../packages/views/cropper/useCropper";

export default function useProduct() {
  const toast = useToast();
  const { t } = i18n.global;
  const { cartIndex } = useRoutes();
  const { getCartProductByIndex } = useCart();
  const { uploadUploaderFiles } = useFileUploader(); // [PACKAGE] - file uploader
  const { uploadCropperFile } = useCropper(); // [PACKAGE] - cropper
  const { formattedPrice, setCartLocalStorage, loadCartFromLocalStorage } =
    useCart();

  // computed
  const showStandText = computed(() => showStandTextInput());

  const showPackagingText = computed(() => showPackagingTextInput());

  const imageUrl = computed(() => getImage());

  const imageFile = computed(() => getImageFile());

  const totalProductPrice = computed(() => getComputedProductPrice());

  function getComputedProductPrice() {
    const product = store.getters["product/getProduct"];

    return getTotalProductPrice(product);
  }

  // product price + packaging
  function getTotalProductPrice(product) {
    let { price } = product;

    price *= product.qty;

    return formattedPrice(price);
  }

  function getImage() {
    const cropperImage = store.getters["cropper/getFile"];
    const originalImage = store.getters["fileUploader/getFiles"];

    if (cropperImage) {
      return cropperImage;
    }
    if (originalImage) {
      return originalImage;
    }

    return null;
  }

  function getImageFile() {
    const product = store.getters["product/getProduct"];

    if (product.files.croppedFile) {
      return product.files.croppedFile;
    }
    if (product.files.originalFile) {
      return product.files.originalFile;
    }

    return {};
  }

  function setProduct(product) {
    const cartProductIndex = getParamFromUrl("edit");
    let commitMethod = "product/SETUP_PRODUCT";
    let cartProduct = null;
    // has edit param in url
    if (cartProductIndex) {
      // retrieve product from cart by index
      cartProduct = getCartProductByIndex(cartProductIndex);
      // not expired cart
      if (cartProduct) {
        // editing product
        commitMethod = "product/SET_PRODUCT";
        cartProduct.cartIndex = cartProductIndex;
        // setting for spotify
        if (cartProduct.category_id === musicCategoryId) {
          presetEditMusicProduct(cartProduct);
        } else if (cartProduct.category_id === customCategoryId) {
          presetEditCustomProduct(cartProduct);
        }

        presetEditUploaders(cartProduct);
      } else {
        // expired cart - clear edit param from url
        updateUrl(location.origin + location.pathname);
      }
    }
    // editing product / creating product
    store.commit(commitMethod, cartProduct || JSON.parse(product));

    calculateVariantPrice(store.getters["product/getProduct"]);
  }

  function setAttributes(attributes) {
    const commitMethod = "product/SET_ATTRIBUTES";

    attributes = JSON.parse(attributes);

    store.commit(commitMethod, attributes);
  }

  function invalidSubmitProduct({ values, errors, results }) {
    const unsavedFile = errors.unsaved_profile || errors.unsaved_file;

    if (unsavedFile) {
      toast.error(unsavedFile);

      scrollToSection(".form-fields");

      return;
    }

    if (errors.original_file) {
      toast.error(errors.original_file);
    } else {
      toast.error(t("fill_required_fields"));
    }

    scrollToSection("#app");
  }

  // [TODO] - update, ak sa jedna o editaciu o produktu
  async function validSubmitProduct() {
    const product = store.getters["product/getProduct"];
    let commitMethod;

    if (product.cartIndex) {
      // update product
      commitMethod = "cart/UPDATE_PRODUCT_IN_CART";
    } else {
      // add product
      commitMethod = "cart/ADD_PRODUCT_TO_CART";
    }

    scrollToSection("body");

    await uploadImage(product);

    loadCartFromLocalStorage();

    store.commit(commitMethod, product);

    setCartLocalStorage();

    window.location.href = cartIndex(1);
  }

  // for input
  function updatedInputField(event) {
    const product = store.getters["product/getProduct"];

    product.attributes[event.name] = event.value;

    store.commit("product/SET_PRODUCT", product);
  }

  async function updateProductAttributeValue(attribute, value) {
    const product = store.getters["product/getProduct"];
    // set new value
    if (product.attribute_values[attribute] !== value) {
      product.attribute_values[attribute] = value;

      checkAvailability(product);

      // checking stock and getting variant id with price
      await calculateVariantPrice(product); // axios
    }
  }

  // Actions
  async function calculateVariantPrice(product) {
    const dispatchMethod = "product/CALCULATE_PRICE";

    await store.dispatch(dispatchMethod).then(({ data }) => {
      if (!data.error) {
        product.product_variant_id = data.product_variant.id;
        product.price = data.product_variant.price;
      }
    });
  }

  function toggleHideCheckbox(event, attribute) {
    const isChecked = event.target.checked;

    if (!isChecked) {
      const product = store.getters["product/getProduct"];

      delete product.attributes[attribute];
    }
  }

  function toggleCheckbox(event, attribute) {
    const product = store.getters["product/getProduct"];

    product.attributes[attribute] = event.target.checked;
  }

  function showStandTextInput() {
    const product = store.getters["product/getProduct"];

    const showInput =
      product.attribute_values.stand.id === engravedOakMassifId ||
      product.attribute_values.stand.id === engravedBlackOakMassifId;

    if (!showInput) {
      product.attributes.standText = null;
    }

    return showInput;
  }

  function showPackagingTextInput() {
    const product = store.getters["product/getProduct"];

    const showInput =
      product.attribute_values.packaging.id === giftDedicationPackagingId;

    if (!showInput) {
      product.attributes.packagingText = null;
    }

    return showInput;
  }

  // presets for spotify
  function presetEditMusicProduct(cartProduct) {
    const openQr = !!cartProduct.attributes.qr;
    const openTimeline = !!cartProduct.attributes.actualTime;

    store.commit("product/SET_SHOW_QR", openQr);
    store.commit("product/SET_SHOW_TIMELINE", openTimeline);
  }

  // preset for custom photo
  function presetEditCustomProduct(cartProduct) {
    const openSecondaryText = !!cartProduct.attributes.secondaryText;

    store.commit("product/SET_SECONDARY_TEXT", openSecondaryText);
  }

  // preset uploader files
  async function presetEditUploaders(cartProduct) {
    // photo
    if (cartProduct.category_id === photoCategoryId) {
      for (const [key, file] of Object.entries(cartProduct.files)) {
        presetEditPhotoFiles(file, key, "photoUploader/", "photoCropper/");
      }
    } else {
      // default
      presetEditFiles(
        cartProduct,
        "fileUploader/",
        "cropper/",
        "originalFile",
        "croppedFile"
      );

      // instagram
      if (cartProduct.category_id === instaCategoryId) {
        presetEditFiles(
          cartProduct,
          "instaUploader/",
          "instaCropper/",
          "originalProfile",
          "croppedProfile"
        );
      }
    }
  }

  async function presetEditPhotoFiles(
    file,
    key,
    uploadNamespace,
    cropperNamespace
  ) {
    let index;

    if (key.includes("originalCollage")) {
      index = key.split("originalCollage").pop();
      // original file
      store.commit(`${uploadNamespace}SET_FILE`, {
        index,
        value: await imageUrlToBase64(file.url),
      });
      store.commit(`${uploadNamespace}SET_SUBMITTED`, {
        index,
        value: true,
      });
    } else {
      // cropped file
      index = key.split("croppedCollage").pop();

      store.commit(`${cropperNamespace}SET_FILE`, {
        index,
        value: await imageUrlToBase64(file.url),
      });
    }
  }

  async function presetEditFiles(
    product,
    uploaderNamespace,
    cropperNamespace,
    uploaderKey,
    cropperKey
  ) {
    store.commit(`${uploaderNamespace}SET_SUBMITTED`, true);
    store.commit(
      `${uploaderNamespace}SET_FILES`,
      await imageUrlToBase64(product.files[uploaderKey].url)
    );
    store.commit(
      `${cropperNamespace}SET_FILE`,
      await imageUrlToBase64(product.files[cropperKey].url)
    );
  }

  // upload files
  async function uploadImage(product) {
    let requests = [];

    store.commit(
      "loader/SET_LOADER",
      product.category_id === accessoryCategoryId
        ? t("accessory_loading")
        : t("image_loading")
    );

    if (product.cartIndex) {
      requests = uploadProductFilesOnEdit(product);
    } else {
      requests = uploadProductFilesOnCreate(product);
    }

    await axios.all(requests);
  }

  // process editing files on editing product in cart
  function uploadProductFilesOnEdit(product) {
    let requests = [];
    // photo product
    if (product.category_id === photoCategoryId) {
      store.getters["photoCropper/getFiles"].map((cropperFile, index) => {
        requests = appendEditRequest(
          requests,
          cropperFile,
          `croppedCollage${index}`,
          `originalCollage${index}`
        );
      });
    } else if (product.category_id !== accessoryCategoryId) {
      // default product
      requests = appendEditRequest(
        requests,
        store.getters["cropper/getFile"],
        "croppedFile",
        "originalFile"
      );

      // instagram product
      if (product.category_id === instaCategoryId) {
        requests = appendEditRequest(
          requests,
          store.getters["instaCropper/getFile"],
          "croppedProfile",
          "originalForiginalProfileile"
        );
      }
    }

    return requests;
  }

  // process sotring files on adding product to cart
  function uploadProductFilesOnCreate(product) {
    let requests = [];
    // photo product
    if (product.category_id === photoCategoryId) {
      store.getters["photoCropper/getFiles"].map((cropperFile, index) => {
        requests = appendCreateRequest(
          requests,
          cropperFile,
          `croppedCollage${index}`,
          `originalCollage${index}`
        );
      });
    } else if (product.category_id !== accessoryCategoryId) {
      // default product files
      requests = appendCreateRequest(
        requests,
        store.getters["cropper/getFile"],
        "croppedFile",
        "originalFile"
      );
      // instagram product
      if (product.category_id === instaCategoryId) {
        requests = appendCreateRequest(
          requests,
          store.getters["instaCropper/getFile"],
          "croppedProfile",
          "originalProfile"
        );
      }
    }
    return requests;
  }

  // append files to upload on edit cart product
  function appendEditRequest(
    requests,
    cropperFile,
    croppedFileKey,
    originalFileSelector
  ) {
    // append original file
    requests.push(uploadCropperFile(croppedFileKey, cropperFile));
    // append cropped file
    if (document.querySelector(`#${originalFileSelector}`).files[0]) {
      requests.push(uploadUploaderFiles(originalFileSelector));
    }

    return requests;
  }

  // append files to upload on edit cart product
  function appendCreateRequest(
    requests,
    cropperFile,
    croppedFileKey,
    originalFileSelector
  ) {
    // append original and cropped file
    requests.push(
      uploadUploaderFiles(originalFileSelector),
      uploadCropperFile(croppedFileKey, cropperFile)
    );

    return requests;
  }

  function soldProductWithAttribute(product, attributeId) {
    // big/small size
    const soldAttributes = [
      // packaging
      giftDedicationPackagingId,
      // stand
      // magneticStand,
      // acrylicStandId,
      // blackOakMassifId,
    ];

    // gift voucher
    if (giftVoucherIds.includes(product.id)) {
      return false;
    }

    // big size
    if (product.attribute_values?.size.id === bigSizeId) {
      // sold gift package, dedication gift packaging
      soldAttributes.push(
        // packaging
        giftPackagingId
      );
    }

    return soldAttributes.includes(attributeId);
  }

  // [TODO] Should load from backend
  function checkAvailability(product) {
    const attributes = store.getters["product/getAttributes"];

    let isSold = false;

    // sold stand
    if (soldProductWithAttribute(product, product.attribute_values.stand.id)) {
      product.attribute_values.stand = attributes.stand.attribute_values[0];

      isSold = true;
    }
    // sold packaging
    if (
      soldProductWithAttribute(product, product.attribute_values.packaging.id)
    ) {
      product.attribute_values.packaging =
        attributes.packaging.attribute_values[0];

      isSold = true;
    }

    if (isSold) {
      // show flash message
      toast.error(t("unavailable_variant"));
    }
  }

  // get attribute tooltip text
  function getAttributeTooltipText(language, product, attributeValue) {
    if (!product.attribute_values?.size) {
      return undefined;
    }

    const { id } = product.attribute_values.size.preferences;
    const domain = capitalize(language.domain);

    const key = `${id}${domain}Price`;

    // default text
    let text = t(attributeValue.slug);

    // append price
    if (attributeValue.preferences[key]) {
      text = `${text} (+${formattedPrice(attributeValue.preferences[key])})`;
    }

    return text;
  }

  return {
    updateProductAttributeValue,
    updatedInputField,
    setAttributes,
    setProduct,
    totalProductPrice,
    getTotalProductPrice,
    calculateVariantPrice,
    // computed
    imageFile,
    imageUrl,
    // submit form
    invalidSubmitProduct,
    validSubmitProduct,
    // custom
    toggleHideCheckbox,
    toggleCheckbox,
    showStandText,
    showPackagingText,

    soldProductWithAttribute,
    getAttributeTooltipText,
  };
}
